import { useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  REPEATING_DST_RESAVE_HELP,
  REPEATING_INTERVAL_HELP,
  REPEATING_LEGACY_TZ_HELP,
} from '../utils/scheduleDefaults';
import {
  type TimezoneOption,
  filterTimezoneOptions,
  getBrowserTimezone,
  getTimezoneOptions,
  resolveTimezoneOption,
} from '../utils/timezoneData';

interface RepeatingScheduleConfigProps {
  value: {
    frequency: number;
    frequencyUnit: 'minute' | 'hour';
    startingAt: { hour: number; minute: number } | null;
    timeZone: string;
  };
  onChange: (value: {
    frequency: number;
    frequencyUnit: 'minute' | 'hour';
    startingAt: { hour: number; minute: number } | null;
    timeZone: string;
  }) => void;
  error?: string;
  disabled?: boolean;
  /** Legacy rows without stored timeZone — do not auto-fill browser TZ on load. */
  preserveEmptyTimeZone?: boolean;
}

// Match legacy: multiples of 5 from 5–59
const MINUTE_FREQUENCIES = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
// Match dataProtect: range(1, 24) hours
const HOUR_FREQUENCIES = Array.from({ length: 24 }, (_, i) => i + 1);
const HOURS_12 = Array.from({ length: 12 }, (_, i) => (i === 0 ? 12 : i));
// Match legacy: full minute granularity for starting time
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export const RepeatingScheduleConfig = ({
  value,
  onChange,
  error,
  disabled = false,
  preserveEmptyTimeZone = false,
}: RepeatingScheduleConfigProps) => {
  const hasStartingTime = value.startingAt !== null;
  const [timezoneOptions] = useState<TimezoneOption[]>(() => getTimezoneOptions());

  const handleFrequencyChange = (event: SelectChangeEvent<number>) => {
    onChange({ ...value, frequency: event.target.value as number });
  };

  const handleFrequencyUnitChange = (event: SelectChangeEvent<'minute' | 'hour'>) => {
    const newUnit = event.target.value as 'minute' | 'hour';
    const defaultFrequency = newUnit === 'minute' ? 15 : 1;
    onChange({ ...value, frequencyUnit: newUnit, frequency: defaultFrequency });
  };

  const handleStartingTimeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    const nextStartingAt = checked ? { hour: 0, minute: 0 } : null;
    const shouldDefaultTimeZone = checked && !preserveEmptyTimeZone && !value.timeZone.trim();
    onChange({
      ...value,
      startingAt: nextStartingAt,
      timeZone: shouldDefaultTimeZone ? getBrowserTimezone() : value.timeZone,
    });
  };

  const handleStartingHourChange = (event: SelectChangeEvent<number>) => {
    if (!value.startingAt) return;
    const hour12 = event.target.value as number;
    const isPM = value.startingAt.hour >= 12;
    let hour24: number;
    if (hour12 === 12) {
      hour24 = isPM ? 12 : 0;
    } else {
      hour24 = isPM ? hour12 + 12 : hour12;
    }
    onChange({ ...value, startingAt: { ...value.startingAt, hour: hour24 } });
  };

  const handleStartingMinuteChange = (event: SelectChangeEvent<number>) => {
    if (!value.startingAt) return;
    onChange({
      ...value,
      startingAt: { ...value.startingAt, minute: event.target.value as number },
    });
  };

  const handleStartingAmPmChange = (event: SelectChangeEvent<string>) => {
    if (!value.startingAt) return;
    const isPM = event.target.value === 'PM';
    let hour24 = value.startingAt.hour % 12;
    if (isPM) hour24 += 12;
    onChange({ ...value, startingAt: { ...value.startingAt, hour: hour24 } });
  };

  const handleTimezoneChange = (_event: React.SyntheticEvent, newValue: TimezoneOption | null) => {
    if (newValue?.value) {
      onChange({ ...value, timeZone: newValue.value });
    }
  };

  const get12Hour = () => {
    if (!value.startingAt) return 12;
    const h = value.startingAt.hour % 12;
    return h === 0 ? 12 : h;
  };

  const getAmPm = () => {
    if (!value.startingAt) return 'AM';
    return value.startingAt.hour >= 12 ? 'PM' : 'AM';
  };

  const frequencies = value.frequencyUnit === 'minute' ? MINUTE_FREQUENCIES : HOUR_FREQUENCIES;
  const selectedTimezone = resolveTimezoneOption(value.timeZone, timezoneOptions);
  const showDstResaveHint = hasStartingTime && !!value.timeZone.trim();

  const getPreviewText = () => {
    const unit = value.frequencyUnit === 'minute' ? 'minutes' : 'hours';
    const freq = value.frequency === 1 ? value.frequencyUnit : `${value.frequency} ${unit}`;

    if (value.startingAt) {
      const tzLabel = value.timeZone || getBrowserTimezone();
      return `Every ${freq}, starting at ${get12Hour()}:${value.startingAt.minute.toString().padStart(2, '0')} ${getAmPm()} in ${tzLabel}`;
    }
    return `Every ${freq}`;
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info" sx={{ py: 0.5 }}>
        {REPEATING_INTERVAL_HELP}
      </Alert>

      {preserveEmptyTimeZone && hasStartingTime && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          {REPEATING_LEGACY_TZ_HELP}
        </Alert>
      )}

      {showDstResaveHint && (
        <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
          {REPEATING_DST_RESAVE_HELP}
        </Alert>
      )}

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Repeat every
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 80 }} disabled={disabled}>
            <InputLabel>Every</InputLabel>
            <Select value={value.frequency} onChange={handleFrequencyChange} label="Every">
              {frequencies.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100 }} disabled={disabled}>
            <Select value={value.frequencyUnit} onChange={handleFrequencyUnitChange}>
              <MenuItem value="minute">Minutes</MenuItem>
              <MenuItem value="hour">Hours</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={hasStartingTime}
              onChange={handleStartingTimeToggle}
              disabled={disabled}
            />
          }
          label="Start first execution at a specific time"
        />

        {hasStartingTime && value.startingAt && (
          <Box sx={{ ml: 4, mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 70 }} disabled={disabled}>
                <InputLabel>Hour</InputLabel>
                <Select value={get12Hour()} onChange={handleStartingHourChange} label="Hour">
                  {HOURS_12.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h.toString().padStart(2, '0')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="h6">:</Typography>

              <FormControl size="small" sx={{ minWidth: 70 }} disabled={disabled}>
                <InputLabel>Min</InputLabel>
                <Select
                  value={value.startingAt.minute}
                  onChange={handleStartingMinuteChange}
                  label="Min"
                >
                  {MINUTES.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m.toString().padStart(2, '0')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 70 }} disabled={disabled}>
                <Select value={getAmPm()} onChange={handleStartingAmPmChange}>
                  <MenuItem value="AM">AM</MenuItem>
                  <MenuItem value="PM">PM</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>
        )}
      </Box>

      {hasStartingTime && (
        <Box>
          <Autocomplete
            value={selectedTimezone}
            onChange={handleTimezoneChange}
            options={timezoneOptions.filter((tz) => tz.value !== '')}
            filterOptions={filterTimezoneOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            getOptionDisabled={(option) => option.value === ''}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Timezone"
                placeholder="Select timezone"
                error={!!error}
                helperText={error}
              />
            )}
            disabled={disabled}
            size="small"
          />
        </Box>
      )}

      <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{getPreviewText()}</strong>
        </Typography>
      </Box>
    </Stack>
  );
};
